import { useState, useMemo } from 'react';
import { Card, DatePicker, Button, Space, Typography, Row, Col } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import type { ColDef } from 'ag-grid-community';
import { DataGrid } from './DataGrid';
import { apiClient } from '../api/client';
import type { ReportConfig, ColumnConfig } from '../types/panelConfig';

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

interface GenericReportPageProps {
  report: ReportConfig;
}

// Currency formatter for TRY
const currencyFormatter = new Intl.NumberFormat('tr-TR', {
  style: 'currency',
  currency: 'TRY',
  minimumFractionDigits: 2,
});

const numberFormatter = new Intl.NumberFormat('tr-TR', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

const percentFormatter = new Intl.NumberFormat('tr-TR', {
  style: 'percent',
  minimumFractionDigits: 2,
});

function formatValue(col: ColumnConfig, value: unknown): string {
  if (value == null) return '';
  if (col.cellRenderer === 'currency' || col.type === 'currency') {
    return currencyFormatter.format(Number(value));
  }
  if (col.cellRenderer === 'percentage' || col.type === 'percentage') {
    return percentFormatter.format(Number(value) / 100);
  }
  if (col.type === 'number') {
    return numberFormatter.format(Number(value));
  }
  if (col.type === 'date' && col.format) {
    return dayjs(String(value)).format(col.format);
  }
  return String(value);
}

function getCellValueFormatter(col: ColumnConfig) {
  if (col.type === 'currency' || col.cellRenderer === 'currency' ||
      col.type === 'percentage' || col.cellRenderer === 'percentage' ||
      col.type === 'number') {
    return (params: { value: unknown }) => formatValue(col, params.value);
  }
  if (col.type === 'date' && col.format) {
    return (params: { value: unknown }) => formatValue(col, params.value);
  }
  return undefined;
}

function buildAgGridColumns(columns: ColumnConfig[]): ColDef[] {
  return columns.map((col) => ({
    field: col.field,
    headerName: col.header,
    width: col.width || undefined,
    pinned: col.pinned ? ('left' as const) : undefined,
    filter: col.filterable,
    sortable: true,
    resizable: true,
    valueFormatter: getCellValueFormatter(col),
    aggFunc: col.agg || undefined,
    cellStyle:
      col.type === 'currency' || col.type === 'number' || col.type === 'percentage'
        ? { textAlign: 'right' }
        : undefined,
  }));
}

export function GenericReportPage({ report }: GenericReportPageProps) {
  const hasDateParams = report.parameters.some((p) => p.type === 'date');
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>([
    dayjs(),
    dayjs(),
  ]);

  const queryParams = useMemo(() => {
    if (!hasDateParams) return {};
    const params: Record<string, string> = {};
    for (const param of report.parameters) {
      if (param.type === 'date' && param.key.toLowerCase().includes('baslangic')) {
        params[param.key] = dateRange[0].format('YYYY-MM-DD');
      } else if (param.type === 'date' && param.key.toLowerCase().includes('bitis')) {
        params[param.key] = dateRange[1].format('YYYY-MM-DD');
      }
    }
    return params;
  }, [hasDateParams, dateRange, report.parameters]);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['report', report.code, report.sourceId, queryParams],
    queryFn: async () => {
      if (!report.sourceId) return [];
      const token = localStorage.getItem('token');
      const { data: response } = await apiClient.post(
        '/api/v1/query',
        { sourceId: report.sourceId, parameters: queryParams },
        { headers: token ? { Authorization: `Bearer ${token}` } : undefined }
      );
      return response.data || [];
    },
    enabled: !!report.sourceId,
  });

  const columns = useMemo(() => buildAgGridColumns(report.columns), [report.columns]);

  // Calculate aggregation footer
  const aggregations = useMemo(() => {
    if (!data || data.length === 0) return [];
    return report.columns
      .filter((col) => col.agg)
      .map((col) => {
        const values = data.map((row: Record<string, unknown>) => Number(row[col.field]) || 0);
        let result: number;
        switch (col.agg) {
          case 'sum':
            result = values.reduce((a: number, b: number) => a + b, 0);
            break;
          case 'avg':
            result = values.reduce((a: number, b: number) => a + b, 0) / values.length;
            break;
          case 'count':
            result = values.length;
            break;
          case 'min':
            result = Math.min(...values);
            break;
          case 'max':
            result = Math.max(...values);
            break;
          default:
            result = 0;
        }
        return {
          label: col.header,
          value: formatValue(col, result),
          agg: col.agg,
        };
      });
  }, [data, report.columns]);

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={4} style={{ margin: 0 }}>
            {report.name}
          </Title>
        </Col>
        <Col>
          <Space>
            {hasDateParams && (
              <RangePicker
                value={dateRange}
                onChange={(dates) => {
                  if (dates?.[0] && dates?.[1]) {
                    setDateRange([dates[0], dates[1]]);
                  }
                }}
                format="DD.MM.YYYY"
              />
            )}
            <Button icon={<ReloadOutlined />} onClick={() => refetch()}>
              Yenile
            </Button>
          </Space>
        </Col>
      </Row>

      {!report.sourceId && (
        <Card>
          <Text type="secondary">
            Bu rapor henüz yapılandırılmamış. Agent kurulumunu tamamlayın.
          </Text>
        </Card>
      )}

      {report.sourceId && (
        <>
          <DataGrid
            data={data || []}
            columns={columns}
            loading={isLoading}
            height="calc(100vh - 280px)"
            pageSize={50}
          />

          {aggregations.length > 0 && (
            <Card size="small" style={{ marginTop: 8 }}>
              <Space size="large">
                {aggregations.map((agg) => (
                  <Text key={agg.label} strong>
                    {agg.label}: {agg.value}
                  </Text>
                ))}
              </Space>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
